import Keycloak from 'keycloak-js';

/**
 * Singleton Keycloak client for the AutoTrack SPA.
 *
 * Configuration is sourced from REACT_APP_KEYCLOAK_* env vars (set in
 * autotrack/environments/.env*). The SPA client (`autotrack-web`) is a public
 * OIDC client with PKCE — there is no client secret here.
 *
 * The instance is intentionally module-scoped (not React state) so that any
 * component, the ApiInstance interceptor, or a non-React module can read
 * `keycloak.token` without prop-drilling or context plumbing.
 */
const url = process.env.REACT_APP_KEYCLOAK_URL;
const realm = process.env.REACT_APP_KEYCLOAK_REALM;
const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

if (!url || !realm || !clientId) {
    // Surface the misconfiguration loudly at startup so a missing env var
    // never silently falls back to a hard-coded default.
    // eslint-disable-next-line no-console
    console.error(
        '[keycloak] Missing one of REACT_APP_KEYCLOAK_URL / _REALM / _CLIENT_ID. ' +
            'Check autotrack/environments/.env*'
    );
}

export const keycloak = new Keycloak({
    url: url ?? '',
    realm: realm ?? '',
    clientId: clientId ?? '',
});

/**
 * `initOptions` is exported so tests and `index.tsx` can reuse the same
 * configuration.
 *
 * `onLoad: 'check-sso'` — silently checks for an existing session without
 * redirecting. The user stays on the LoginPage and explicitly clicks
 * "Continue with Google/Facebook" to trigger `keycloak.login()`.
 *
 * `checkLoginIframe: false` — disables the hidden 3rd-party iframe that
 * keycloak-js would otherwise use for the SSO check. That iframe is
 * blocked by many browsers (Safari, Firefox ETP, Brave, Chrome
 * Incognito) due to third-party cookie restrictions, causing a timeout
 * error that stalls bootstrap. Since we combine it with `check-sso`,
 * init resolves immediately with `authenticated=false`, and the iframe
 * would have nothing to check anyway.
 *
 * `scope` must include `openid` for ID tokens; we add `email` and `profile`
 * so social IdPs (Google/Facebook) forward those claims back to Keycloak
 * (and from there into the AutoTrack userInfo). Without these, the
 * /auth/callback flow doesn't have enough claims to hydrate the user.
 */
export const keycloakInitOptions: Keycloak.KeycloakInitOptions = {
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    scope: 'openid email profile',
    // Disable the 3rd-party iframe check entirely. With `onLoad:
    // 'login-required'` the app redirects to Keycloak on every cold
    // load anyway, so the silent SSO iframe is pure overhead. Worse,
    // browsers with third-party cookie restrictions (Safari, Firefox
    // ETP, Chrome Incognito, Brave) block the iframe's postMessage,
    // causing a timeout error that stalls the whole bootstrap.
    checkLoginIframe: false,
};

/**
 * Helper: refresh the access token if it expires within `minValidity` seconds.
 * Returns the (possibly refreshed) token, or null if the user is not
 * authenticated.
 */
export async function getValidToken(minValidity = 30): Promise<string | null> {
    if (!keycloak.authenticated) return null;
    try {
        const refreshed = await keycloak.updateToken(minValidity);
        if (refreshed) {
            // eslint-disable-next-line no-console
            console.debug('[keycloak] access token refreshed');
        }
        return keycloak.token ?? null;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[keycloak] failed to refresh token', err);
        return null;
    }
}
